import type { ConsentCategory, ConsentChoices, ConsentRecord } from "./types";
import { CONSENT_CATEGORIES, NOTHING_ALLOWED } from "./types";

/**
 * Where the visitor's cookie choice lives.
 *
 * localStorage, not a cookie: the choice is only ever read in the browser, and a
 * cookie would be sent to the server on every request for no reason. Storing a
 * preference this way is itself covered by the strictly-necessary category,
 * which is why that category cannot be switched off.
 *
 * Reads NEVER throw. An unreadable or hand-edited value must land on "nothing
 * allowed yet", which shows the banner again: a corrupt value must not turn
 * tracking on. An answer given THIS page load is not that, and survives a store
 * that refused to keep it — see `decidedThisPageLoad`.
 */

const KEY = "eup-cookie-consent";

/** An answer the visitor gave that storage refused to keep. Never a guess:
 * only `writeConsent` sets it, and only when the write failed. */
let decidedThisPageLoad: ConsentRecord | null = null;

/** The storage path builds a fresh record every read, so the memory path must
 * too: a caller mutating what it got back must not change the next read. */
const copyOf = (record: ConsentRecord): ConsentRecord => ({
  ...record,
  choices: { ...record.choices },
  asked: [...record.asked],
});

/**
 * localStorage, or null when there isn't one.
 *
 * Reached through a cast because the DOM types insist `localStorage` always
 * exists, and here it genuinely may not: on the server it is undefined, and in a
 * locked-down or private window touching it throws. Both have to read as "no
 * stored consent", which shows the banner again.
 */
function storage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** What storage holds, or null when it holds nothing readable. */
function readStored(): ConsentRecord | null {
  const store = storage();
  if (!store) return null;
  let raw: string | null;
  try {
    // Separate from the parse below only to say which failed; both are "no
    // readable answer" to the caller.
    raw = store.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    // Treated as untyped input, not cast to the record type: JSON.parse happily
    // returns a string, a number or null, and a hand-edited value must not walk
    // straight into the choices below.
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;

    const storedChoices = record.choices;
    if (typeof storedChoices !== "object" || storedChoices === null)
      return null;
    const stored = storedChoices as Record<string, unknown>;

    const choices = { ...NOTHING_ALLOWED };
    for (const key of Object.keys(choices) as (keyof ConsentChoices)[]) {
      // `=== true` and nothing looser: "true", 1 and {} are not a yes.
      choices[key] = stored[key] === true;
    }

    const asked = Array.isArray(record.asked)
      ? (record.asked as unknown[]).filter((value): value is ConsentCategory =>
          (CONSENT_CATEGORIES as readonly unknown[]).includes(value),
        )
      : [];

    return {
      choices,
      asked,
      decidedAt: typeof record.decidedAt === "number" ? record.decidedAt : 0,
    };
  } catch {
    return null;
  }
}

/**
 * The most recent decision wins, wherever it lives. A tie goes to storage,
 * which at least persisted.
 */
export function readConsent(): ConsentRecord | null {
  const stored = readStored();
  const remembered = decidedThisPageLoad;
  if (!stored) return remembered && copyOf(remembered);
  if (!remembered) return stored;
  return remembered.decidedAt > stored.decidedAt ? copyOf(remembered) : stored;
}

export function writeConsent(record: ConsentRecord): void {
  // Guarded on `window`: one module instance serves every request on the
  // server, so a value kept there reaches the next visitor.
  const remember = () => {
    // A copy: callers pass their own objects (ConsentProvider hands over the
    // `offered` array and constants like EVERYTHING_ALLOWED).
    if (typeof window !== "undefined") decidedThisPageLoad = copyOf(record);
  };
  const store = storage();
  if (!store) {
    remember();
    return;
  }
  try {
    store.setItem(KEY, JSON.stringify(record));
    // Storage holds it, so memory must not become a second source of truth.
    decidedThisPageLoad = null;
  } catch {
    // Full or blocked, so a module-level reader still sees the answer.
    remember();
  }
}

/** Test-only: module state must not leak between cases in a suite. */
export function resetDecidedThisPageLoadForTests(): void {
  decidedThisPageLoad = null;
}

/**
 * True when the visitor still has to be asked — either they never answered, or
 * the site now offers a category that did not exist when they did.
 */
export function needsAsking(
  record: ConsentRecord | null,
  offered: ConsentCategory[],
): boolean {
  if (!record) return true;
  const asked = new Set(record.asked);
  return offered.some((category) => !asked.has(category));
}

/**
 * Is this category allowed right now, for code outside React that cannot use
 * the hook — a plain analytics helper, an event handler in a module.
 *
 * Reads through `readConsent`, so it sees an answer a store refused to keep.
 * Same rule as the provider's `allows`: the visitor must have been ASKED about
 * the category and have allowed it, so a stored yes nobody was shown does not
 * count.
 */
export function isConsentedTo(category: ConsentCategory): boolean {
  if (category === "necessary") return true;
  const stored = readConsent();
  if (!stored) return false;
  if (!stored.asked.includes(category)) return false;
  // `category` is narrowed past "necessary" by the early return above.
  return stored.choices[category] === true;
}
